// AGC: Text Note Modal - Create text notes with speech-to-text, media attachments,
// AGC: team mentions, lot selection, and auto-GPS capture

import React, { useState, useRef, useCallback, useEffect } from 'react'
import { X, Check, MapPin, Users, Loader2, Mic, Send, Camera, Video, Trash2 } from 'lucide-react'
import { Button } from '../shared'
import { useTranslation } from '../../hooks/useTranslation'
import { useLotes } from '../../context'
import { useSubscription } from '../../context/SubscriptionContext'
import { openInGoogleMaps, formatCoordinates } from '../../utils/gpsUtils'
import { getBackendUrl } from '../../utils/backendUrl'
import type { GPSLocation } from '../../types/lotNotes'
import toast from 'react-hot-toast'

interface NoteAttachment {
  file: Blob
  type: 'photo' | 'video' | 'audio'
  previewUrl: string
}

interface TextNoteModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (
    content: string,
    lotIds: string[],
    taggedUserIds: string[],
    gpsLocation?: GPSLocation,
    attachment?: { file: Blob; type: 'photo' | 'video' | 'audio' }
  ) => void
  defaultLotId: string
}

export const TextNoteModal: React.FC<TextNoteModalProps> = ({
  isOpen,
  onClose,
  onSave,
  defaultLotId,
}) => {
  const { t, i18n } = useTranslation()
  const { plots: lotes } = useLotes()
  const { tier } = useSubscription()
  const useWhisper = tier !== 'free'
  const speechLang = i18n?.language === 'en' ? 'en-US' : i18n?.language === 'es' ? 'es-ES' : 'pt-BR'
  const [content, setContent] = useState('')
  const [selectedLotIds, setSelectedLotIds] = useState<string[]>([defaultLotId])
  const [taggedUserIds, setTaggedUserIds] = useState<string[]>([])
  const [showLotSelector, setShowLotSelector] = useState(false)
  const [gpsLocation, setGpsLocation] = useState<GPSLocation | undefined>()
  const [isCapturingGPS, setIsCapturingGPS] = useState(false)
  const [gpsError, setGpsError] = useState(false)

  // AGC: Speech-to-text state
  const [isListening, setIsListening] = useState(false)
  const [isProcessingSpeech, setIsProcessingSpeech] = useState(false)

  // AGC: Attachment state
  const [attachment, setAttachment] = useState<NoteAttachment | null>(null)
  const photoInputRef = useRef<HTMLInputElement>(null)
  const videoInputRef = useRef<HTMLInputElement>(null)
  // AGC: Audio recording attachment state
  const [isRecordingAudio, setIsRecordingAudio] = useState(false)
  const [audioSeconds, setAudioSeconds] = useState(0)
  const audioRecorderRef = useRef<MediaRecorder | null>(null)
  const audioStreamRef = useRef<MediaStream | null>(null)
  const audioTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // AGC: Auto-capture GPS when modal opens
  useEffect(() => {
    if (isOpen) {
      captureGPS()
    }
  }, [isOpen])

  const captureGPS = async () => {
    setIsCapturingGPS(true)
    setGpsError(false)
    try {
      const { getCurrentPosition } = await import('../../services/nativeGeolocation')
      const coords = await getCurrentPosition({ enableHighAccuracy: true, timeout: 10000 })
      setGpsLocation({
        latitude: coords.latitude,
        longitude: coords.longitude,
        accuracy: coords.accuracy ?? 0,
        timestamp: new Date().toISOString(),
      })
    } catch {
      setGpsError(true)
    } finally {
      setIsCapturingGPS(false)
    }
  }

  // AGC: Speech-to-text — click to record, click to stop and transcribe
  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const handleSpeechToText = useCallback(async () => {
    if (recorderRef.current) {
      // AGC: Click 2 — stop recording, send to Whisper, set content
      const recorder = recorderRef.current
      if (recorder.state === 'inactive') {
        recorderRef.current = null
        setIsListening(false)
        return
      }

      // AGC: Stop and wait for final data
      const blob = await new Promise<Blob>((resolve) => {
        recorder.onstop = () => {
          streamRef.current?.getTracks().forEach(t => t.stop())
          streamRef.current = null
          recorderRef.current = null
          const mime = recorder.mimeType || 'audio/webm'
          resolve(new Blob(chunksRef.current, { type: mime }))
          chunksRef.current = []
        }
        recorder.stop()
      })

      if (blob.size < 1000) { setIsListening(false); return }

      // AGC: Show processing state while Whisper transcribes
      setIsListening(false)
      setIsProcessingSpeech(true)
      try {
        const backendUrl = getBackendUrl()
        const formData = new FormData()
        formData.append('audio', blob, `speech_${Date.now()}.webm`)
        const resp = await fetch(`${backendUrl}/api/v1/voice/transcribe`, { method: 'POST', body: formData })
        const data = await resp.json()
        if (data.success && data.transcription) {
          setContent(prev => prev ? `${prev} ${data.transcription}` : data.transcription)
        }
      } catch (err) {
        console.error('AGC: Whisper transcription failed', err)
        toast.error(t('notes.speechError') || 'Falha no reconhecimento de voz')
      } finally {
        setIsProcessingSpeech(false)
      }
      return
    }

    // AGC: Click 1 — start recording
    setIsListening(true)
    try {
      if (useWhisper) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        streamRef.current = stream
        const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm'
        const recorder = new MediaRecorder(stream, { mimeType })
        chunksRef.current = []
        recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data) }
        recorder.start(250)
        recorderRef.current = recorder
      } else {
        // AGC: FREE → native speech recognition (auto-stops when user pauses)
        const speech = await import('../../services/nativeSpeechRecognition')
        const hasPerm = await speech.hasPermission()
        if (!hasPerm) {
          const granted = await speech.requestPermission()
          if (!granted) { setIsListening(false); return }
        }
        const result = await speech.recognizeSpeech(speechLang)
        if (result.success && result.transcript) {
          setContent(prev => prev ? `${prev} ${result.transcript}` : result.transcript!)
        }
        setIsListening(false)
      }
    } catch {
      toast.error(t('notes.speechError') || 'Falha no reconhecimento de voz')
      setIsListening(false)
    }
  }, [t, useWhisper, speechLang])

  // AGC: Handle photo/video file selection
  const handleFileSelected = useCallback((e: React.ChangeEvent<HTMLInputElement>, type: 'photo' | 'video') => {
    const file = e.target.files?.[0]
    if (!file) return
    // AGC: Clean up previous attachment preview URL
    if (attachment?.previewUrl) URL.revokeObjectURL(attachment.previewUrl)
    setAttachment({
      file,
      type,
      previewUrl: URL.createObjectURL(file),
    })
    // AGC: Reset input so the same file can be re-selected
    e.target.value = ''
  }, [attachment])

  // AGC: Remove attachment
  const removeAttachment = useCallback(() => {
    if (attachment?.previewUrl) URL.revokeObjectURL(attachment.previewUrl)
    setAttachment(null)
    setIsRecordingAudio(false)
    setAudioSeconds(0)
    if (audioTimerRef.current) clearInterval(audioTimerRef.current)
    if (audioStreamRef.current) { audioStreamRef.current.getTracks().forEach(t => t.stop()); audioStreamRef.current = null }
  }, [attachment])

  // AGC: Start recording audio attachment
  const startAudioRecording = useCallback(async () => {
    try {
      removeAttachment()
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      audioStreamRef.current = stream
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm'
      const recorder = new MediaRecorder(stream, { mimeType })
      const chunks: Blob[] = []
      audioRecorderRef.current = recorder

      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data) }
      recorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop())
        audioStreamRef.current = null
        const blob = new Blob(chunks, { type: mimeType })
        if (blob.size > 100) {
          setAttachment({ file: blob, type: 'audio', previewUrl: URL.createObjectURL(blob) })
        }
        setIsRecordingAudio(false)
        if (audioTimerRef.current) clearInterval(audioTimerRef.current)
      }

      recorder.start(250)
      setIsRecordingAudio(true)
      setAudioSeconds(0)
      const startTime = Date.now()
      audioTimerRef.current = setInterval(() => {
        setAudioSeconds(Math.floor((Date.now() - startTime) / 1000))
      }, 500)
    } catch {
      toast.error('Microfone não disponível')
    }
  }, [removeAttachment])

  // AGC: Stop recording audio attachment
  const stopAudioRecording = useCallback(() => {
    if (audioRecorderRef.current && audioRecorderRef.current.state !== 'inactive') {
      audioRecorderRef.current.stop()
    }
  }, [])

  const toggleLot = (lotId: string) => {
    if (selectedLotIds.includes(lotId)) {
      if (selectedLotIds.length > 1) {
        setSelectedLotIds(selectedLotIds.filter(id => id !== lotId))
      }
    } else {
      setSelectedLotIds([...selectedLotIds, lotId])
    }
  }

  const handleSave = () => {
    if (!content.trim()) return
    onSave(
      content,
      selectedLotIds,
      taggedUserIds,
      gpsLocation,
      attachment ? { file: attachment.file, type: attachment.type } : undefined
    )
    handleClose()
  }

  const handleClose = () => {
    setContent('')
    setSelectedLotIds([defaultLotId])
    setTaggedUserIds([])
    setGpsLocation(undefined)
    setGpsError(false)
    setShowLotSelector(false)
    setIsListening(false)
    removeAttachment()
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* AGC: Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('notes.writeNote')}
          </h2>
          <button
            onClick={handleClose}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* AGC: Content area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* AGC: Text input with mic button */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('notes.noteContent')}
            </label>
            <div className="relative">
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={t('notes.writeYourNote')}
                className="w-full px-4 py-3 pr-12 bg-white dark:bg-slate-800 text-gray-900 dark:text-white border border-gray-300 dark:border-slate-600 rounded-xl placeholder:text-gray-400 focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent min-h-[120px] resize-none"
                autoFocus
              />
              {/* AGC: Mic button for speech-to-text */}
              <button
                onClick={handleSpeechToText}
                disabled={isProcessingSpeech}
                className={`absolute right-2 bottom-2 p-2 rounded-lg transition-all ${
                  isProcessingSpeech
                    ? 'bg-blue-500 cursor-wait'
                    : isListening
                    ? 'bg-red-500'
                    : 'bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600'
                }`}
                title={isProcessingSpeech ? t('voice.processing') : isListening ? t('notes.tapToSend') : t('notes.tapToSpeak')}
              >
                {isProcessingSpeech ? (
                  <Loader2 className="w-5 h-5 text-white animate-spin" />
                ) : isListening ? (
                  <Send className="w-5 h-5 text-white" />
                ) : (
                  <Mic className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                )}
              </button>
            </div>
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {content.length} {t('common.characters')}
              </p>
              {isListening && (
                <p className="text-xs text-red-500">{t('notes.tapToSend')}</p>
              )}
              {isProcessingSpeech && (
                <p className="text-xs text-blue-500">{t('voice.processing')}</p>
              )}
            </div>
          </div>

          {/* AGC: Attachment buttons */}
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
              {t('notes.attachments')}
            </label>
            {isRecordingAudio ? (
              // AGC: Recording audio — show stop button with timer
              <div className="flex items-center gap-3 p-3 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
                <Mic className="w-5 h-5 text-red-500 animate-pulse" />
                <span className="text-sm font-medium text-red-600 dark:text-red-400 tabular-nums">
                  {Math.floor(audioSeconds / 60)}:{(audioSeconds % 60).toString().padStart(2, '0')}
                </span>
                <div className="flex-1" />
                <button
                  onClick={stopAudioRecording}
                  className="px-4 py-1.5 bg-red-500 text-white text-xs font-medium rounded-lg hover:bg-red-600 transition-colors"
                >
                  {t('notes.stopRecording') || 'Parar'}
                </button>
              </div>
            ) : attachment ? (
              // AGC: Attachment preview
              <div className="relative rounded-xl overflow-hidden border border-gray-200 dark:border-slate-600">
                {attachment.type === 'photo' ? (
                  <img src={attachment.previewUrl} alt="" className="w-full h-32 object-cover" />
                ) : attachment.type === 'video' ? (
                  <video src={attachment.previewUrl} controls className="w-full h-32 object-cover" />
                ) : attachment.type === 'audio' ? (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-800">
                    <Mic className="w-5 h-5 text-red-500 shrink-0" />
                    <audio src={attachment.previewUrl} controls className="flex-1 h-8" />
                  </div>
                ) : null}
                <button
                  onClick={removeAttachment}
                  className="absolute top-2 right-2 p-1.5 bg-black/60 rounded-full hover:bg-black/80 transition-colors"
                >
                  <Trash2 className="w-4 h-4 text-white" />
                </button>
              </div>
            ) : (
              // AGC: Attachment picker buttons — photo, video, audio
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => photoInputRef.current?.click()}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-400 text-xs font-medium hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                >
                  <Camera className="w-4 h-4" />
                  {t('notes.attachPhoto')}
                </button>
                <button
                  onClick={() => videoInputRef.current?.click()}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-400 text-xs font-medium hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                >
                  <Video className="w-4 h-4" />
                  {t('notes.attachVideo')}
                </button>
                <button
                  onClick={startAudioRecording}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-400 text-xs font-medium hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                >
                  <Mic className="w-4 h-4" />
                  {t('notes.attachAudio')}
                </button>
              </div>
            )}
          </div>

          {/* AGC: Lot selection */}
          <div>
            <button
              onClick={() => setShowLotSelector(!showLotSelector)}
              className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-[var(--color-accent)] transition-colors"
            >
              <MapPin className="w-4 h-4" />
              {t('notes.attachToLot')} ({selectedLotIds.length})
            </button>

            {showLotSelector && (
              <div className="mt-2 p-3 bg-gray-50 dark:bg-slate-800 rounded-lg max-h-48 overflow-y-auto">
                {lotes.map(lot => (
                  <label
                    key={lot.id}
                    className="flex items-center gap-2 py-2 cursor-pointer hover:bg-white dark:hover:bg-slate-700 rounded px-2 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedLotIds.includes(lot.id)}
                      onChange={() => toggleLot(lot.id)}
                      className="w-4 h-4 rounded border-gray-300 text-[var(--color-accent)] focus:ring-[var(--color-accent)]"
                    />
                    <span className="text-sm text-gray-900 dark:text-white">
                      {lot.name} ({lot.area.toFixed(1)} ha)
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* AGC: GPS auto-captured indicator */}
          <div>
            {gpsLocation ? (
              <button
                onClick={() => openInGoogleMaps(gpsLocation.latitude, gpsLocation.longitude)}
                className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 hover:underline"
              >
                <MapPin className="w-4 h-4" />
                <span>{formatCoordinates(gpsLocation.latitude, gpsLocation.longitude)}</span>
              </button>
            ) : isCapturingGPS ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('notes.capturingGPS')}
              </div>
            ) : gpsError ? (
              <button
                onClick={captureGPS}
                className="flex items-center gap-2 text-sm text-red-500 hover:text-red-600"
              >
                <MapPin className="w-4 h-4" />
                {t('notes.gpsFailed')} — {t('notes.gpsRetry')}
              </button>
            ) : null}
          </div>

          {/* AGC: Team mentions placeholder */}
          <div>
            <button className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-[var(--color-accent)] transition-colors">
              <Users className="w-4 h-4" />
              {t('notes.tagTeamMember')}
            </button>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('notes.tagTeamHint')}
            </p>
          </div>
        </div>

        {/* AGC: Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-slate-700 flex gap-3">
          <Button variant="secondary" onClick={handleClose} fullWidth>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!content.trim()}
            icon={<Check className="w-5 h-5" />}
            fullWidth
          >
            {t('common.save')}
          </Button>
        </div>
      </div>

      {/* AGC: Hidden file inputs for attachments */}
      <input ref={photoInputRef} type="file" accept="image/*" capture="environment" onChange={(e) => handleFileSelected(e, 'photo')} className="hidden" />
      <input ref={videoInputRef} type="file" accept="video/*" capture="environment" onChange={(e) => handleFileSelected(e, 'video')} className="hidden" />
    </div>
  )
}

export default TextNoteModal
